import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import FilterPanel from '@/pages/category/components/FilterPanel';
import {
  allVideos,
  applyFilters,
  sortVideos,
  getCategory,
  SORT_OPTIONS,
  type FilterState,
} from '@/lib/videos';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 12;

const CATEGORY_SEO: Record<string, string> = {
  drama: '男同剧情长片合集，精选多部完整剧情作品，呈现男同群体的情感世界与人生故事。',
  short: '男同短片合集，汇集精炼动人的短片作品，用几组镜头捕捉真实而细腻的瞬间。',
  special: '男同专题合集，通过策划与访谈深度梳理男同影像文化的脉络与记忆。',
};

export default function Category() {
  const { type = 'drama' } = useParams();
  const category = getCategory(type);

  const [filters, setFilters] = useState<FilterState>({ type, year: 'all', duration: 'all' });
  const [sort, setSort] = useState('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setFilters({ type, year: 'all', duration: 'all' });
    setSort('latest');
  }, [type]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [filters, sort]);

  const results = useMemo(() => sortVideos(applyFilters(allVideos, filters), sort), [filters, sort]);
  const shown = results.slice(0, visible);

  usePageMeta({
    title: `男同视频 - ${category?.name ?? '分类'}男同视频合集`,
    description: category ? CATEGORY_SEO[category.key] : '男同视频分类内容合集。',
    keywords: `男同视频,${category?.name ?? ''}男同视频,男同${category?.name ?? ''},男同主题内容`,
  });

  if (!category) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center gap-4 px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line text-3xl" />
        </span>
        <h1 className="text-xl font-bold text-foreground-950">找不到该分类</h1>
        <p className="text-sm text-foreground-500">该分类可能已调整，去首页看看其他精彩内容吧。</p>
        <Link
          to="/"
          className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: category.name }]} />

      <header className="mt-4 flex flex-col gap-2">
        <h1 className="text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
          {category.name}男同视频合集
        </h1>
        <p className="max-w-3xl text-sm leading-relaxed text-foreground-500 md:text-base">
          {CATEGORY_SEO[category.key]}
        </p>
      </header>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200/80 bg-background-50 p-5">
            <FilterPanel filters={filters} onChange={setFilters} onReset={() => setFilters({ type: 'all', year: 'all', duration: 'all' })} />
          </div>
        </aside>

        <section>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-500">
              共 <span className="font-bold text-primary-600">{results.length}</span> 部内容
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="flex items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选
              </button>
              <div className="relative">
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  aria-label="排序方式"
                  className="cursor-pointer appearance-none rounded-full border border-background-300 bg-background-50 py-2 pl-4 pr-9 text-sm font-medium text-foreground-700 focus:border-primary-400 focus:outline-none"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              </div>
            </div>
          </div>

          {results.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-3xl" />
              </span>
              <h3 className="text-base font-bold text-foreground-950">没有符合条件的视频</h3>
              <p className="max-w-sm text-sm text-foreground-500">
                试着放宽年份或时长条件，或重置筛选，查看该分类下的全部内容。
              </p>
              <button
                type="button"
                onClick={() => setFilters({ type: 'all', year: 'all', duration: 'all' })}
                className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {shown.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>

              {visible < results.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-8 py-3 text-sm font-semibold text-primary-600 transition-colors hover:border-primary-300 hover:bg-primary-50"
                  >
                    <i className="ri-add-line" />
                    加载更多（还有 {results.length - visible} 部）
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-foreground-950/40"
          />
          <div className="absolute inset-y-0 right-0 flex w-[82%] max-w-sm flex-col overflow-y-auto bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-bold text-foreground-950">筛选</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-background-100 text-foreground-500"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterPanel
              filters={filters}
              onChange={setFilters}
              onReset={() => setFilters({ type: 'all', year: 'all', duration: 'all' })}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full whitespace-nowrap rounded-full bg-primary-500 py-3 text-sm font-semibold text-background-50"
            >
              查看 {results.length} 个结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}